/**
 * Felix pipeline shapes for the hosted chat-ui (Phase E / E3+E4).
 *
 * The hosted app generates + VIEWS pipelines (the graph is the primary surface);
 * actual runs/renders hand off to a local Felix ("Open in Felix"). This module
 * is the single place that assembles the canonical doc pipeline — mirroring
 * Felix's backend generate.ts — so the Pipeline
 * view (E4) stay in sync.
 */

export interface FelixNodeLite {
  id: string;
  kind: "input" | "extract" | "agent" | "template" | "render" | "output"
      | "transform" | "mcp_call" | "http" | "crawl" | "nexus_query"
      | "file_extract" | "match" | "ocr" | "review";
  label: string;
  position: { x: number; y: number };
  config: Record<string, unknown>;
}

/** JSON-Schema description of a pipeline's parameters (data pipelines declare
 *  this; document pipelines instead take uploaded files). */
export interface FelixInputSchema {
  type?: string;
  properties?: Record<string, { type?: string; description?: string; enum?: string[] }>;
  required?: string[];
}

export interface FelixGraphLite {
  id: string;
  name: string;
  instance: string;
  nodes: FelixNodeLite[];
  edges: { id: string; source: string; target: string }[];
  input_schema?: FelixInputSchema;
}

// New pipelines carry the review checkpoints by default (agentic_review_node
// spec R4) — extraction + artifact gates wired ALONGSIDE the data edges.
const FULL_EDGES = [
  { id: "e1", source: "in", target: "ex" },
  { id: "e2", source: "ex", target: "ag" },
  { id: "e_ex_rv1", source: "ex", target: "rv_extraction" },
  { id: "e_rv1_ag", source: "rv_extraction", target: "ag" },
  { id: "e3", source: "ag", target: "rn" },
  { id: "e4", source: "tpl", target: "rn" },
  { id: "e_rn_rv2", source: "rn", target: "rv_artifact" },
  { id: "e_ag_rv2", source: "ag", target: "rv_artifact" },
  { id: "e_rv2_out", source: "rv_artifact", target: "out" },
  { id: "e5", source: "rn", target: "out" },
];

/** Canonical doc pipeline for a skill: input→extract→agent(skill)→render←template→output,
 *  with review checkpoints (extraction + artifact) gating alongside. */
export function buildCanonicalPipeline(
  skillId: string,
  instance: string,
  opts: { template?: string; loopOver?: string; needsGeneration?: boolean } = {},
): FelixGraphLite {
  const render: Record<string, unknown> = { output_name: `${skillId}.docx` };
  if (opts.loopOver) render.loop_over = opts.loopOver;
  const nodes: FelixNodeLite[] = [
    { id: "in", kind: "input", label: "Upload", position: { x: 40, y: 120 }, config: { files: [] } },
    { id: "ex", kind: "extract", label: "Extract", position: { x: 280, y: 120 }, config: {} },
    { id: "rv_extraction", kind: "review", label: "Review extraction", position: { x: 400, y: 260 }, config: { check: "extraction", on_issue: "block" } },
    { id: "ag", kind: "agent", label: `Skill: ${skillId}`, position: { x: 520, y: 120 }, config: { skill: skillId, skill_arg: "extracted_documents" } },
    { id: "tpl", kind: "template", label: "Template", position: { x: 520, y: 300 }, config: { schema_page: opts.template || "", template_docx: "" } },
    { id: "rn", kind: "render", label: "Render", position: { x: 760, y: 200 }, config: render },
    { id: "rv_artifact", kind: "review", label: "Review documents", position: { x: 880, y: 320 }, config: { check: "artifact", on_issue: "block" } },
    { id: "out", kind: "output", label: "Output", position: { x: 1000, y: 200 }, config: {} },
  ];
  if (opts.needsGeneration === false) {
    // No render step → the extraction gate is the only applicable checkpoint.
    return {
      id: "", name: `${skillId} pipeline`, instance,
      nodes: [nodes[0], nodes[1], nodes[2], nodes[3], nodes[7]],
      edges: [
        { id: "e1", source: "in", target: "ex" },
        { id: "e2", source: "ex", target: "ag" },
        { id: "e_ex_rv1", source: "ex", target: "rv_extraction" },
        { id: "e_rv1_ag", source: "rv_extraction", target: "ag" },
        { id: "e3", source: "ag", target: "out" },
      ],
    };
  }
  return { id: "", name: `${skillId} pipeline`, instance, nodes, edges: FULL_EDGES };
}

